import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Shield, Lock, Clock, Heart, Database, Users } from "lucide-react";
import { motion } from "framer-motion";

const SECTIONS = [
  {
    icon: Database,
    title: "Conservazione dei dati",
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    content: "Tutti i tuoi dati — profili, conversazioni, foto, registrazioni vocali e voci di diario — sono conservati su server sicuri con crittografia a riposo. I backup vengono eseguiti quotidianamente su infrastrutture ridondanti geograficamente distribuite, garantendo la massima sicurezza contro perdita di dati.",
  },
  {
    icon: Clock,
    title: "Durata della conservazione",
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    content: "I dati vengono conservati a tempo indeterminato, finche il tuo account resta attivo o finche il tuo custode designato mantiene attivo il servizio. Non cancelleremo mai i tuoi ricordi senza il tuo consenso esplicito o quello del tuo custode. Puoi impostare una data di auto-cancellazione nelle impostazioni privacy.",
  },
  {
    icon: Heart,
    title: "Attivazione post-evento",
    color: "text-rose-400",
    bg: "bg-rose-400/10",
    content: "Quando designi un custode, questa persona ricevera le istruzioni per attivare la tua presenza digitale. Il custode potra: accedere alle conversazioni AI che hai preparato, condividere i ricordi con i tuoi cari, gestire il tuo profilo digitale. Il custode non potra modificare i contenuti che hai creato — solo tu decidi cosa resta.",
  },
  {
    icon: Lock,
    title: "Sicurezza e crittografia",
    color: "text-violet-400",
    bg: "bg-violet-400/10",
    content: "Utilizziamo crittografia AES-256 per i dati a riposo e TLS 1.3 per i dati in transito. Le password sono protette con hashing bcrypt. I file audio e le foto sono archiviati in storage separato con accesso controllato. Non condividiamo mai i tuoi dati con terze parti per scopi commerciali.",
  },
  {
    icon: Users,
    title: "Controllo e trasparenza",
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    content: "Hai il controllo totale sui tuoi dati. Puoi: scaricare un backup completo in qualsiasi momento, scegliere cosa rendere visibile al tuo custode, eliminare singoli profili o conversazioni, cancellare completamente il tuo account. Ogni modifica alle impostazioni di privacy e immediatamente effettiva.",
  },
  {
    icon: Shield,
    title: "Il nostro impegno",
    color: "text-primary",
    bg: "bg-primary/8",
    content: "IoCiSono.org e un servizio di conservazione emotiva, non un social network. Non monetizziamo i tuoi dati. Non mostriamo pubblicita. Il nostro unico obiettivo e preservare i ricordi e le connessioni umane con la massima cura, rispetto e sicurezza. I tuoi ricordi meritano di essere custoditi per sempre.",
  },
];

export default function PolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-hero" data-testid="policy-page">
      <header className="border-b border-border/40 bg-card/60 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-3.5 flex items-center">
          <Link to="/settings" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="policy-back">
            <ArrowLeft className="w-4 h-4" /> Impostazioni
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-2">Policy di conservazione</h1>
          <p className="text-sm text-muted-foreground">Come proteggiamo e custodiamo i tuoi ricordi nel tempo.</p>
        </motion.div>

        <div className="space-y-4">
          {SECTIONS.map((section, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="bg-card border-border/30 shadow-soft">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl ${section.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <section.icon className={`w-5 h-5 ${section.color}`} />
                    </div>
                    <div>
                      <h2 className="font-serif text-base font-bold text-foreground mb-2">{section.title}</h2>
                      <p className="text-sm text-muted-foreground leading-relaxed">{section.content}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="mt-8 text-center">
          <p className="text-xs text-muted-foreground/50">
            Ultimo aggiornamento: Febbraio 2026 | IoCiSono.org
          </p>
        </motion.div>
      </div>
    </div>
  );
}
